import {GruppeMedlemListeModell, Invitasjonsstatus, LitenIkkeTomStreng, MedlemAssosiasjonerModell, MedlemId, MedlemsRolleId, MedlemsRollePeriode2Modell, Medlemstatus, TilgangsrolleMedlem} from "../tjenester/ressurser/generertNg2HttpKlient";
import {EnumOversettelser} from "felles-ng2/tjenester/i18n/EnumOversettelser";
import {navnPåEnuminnslag} from "felles-ng2/støttefunksjoner/enumer";
import {første} from "felles-ng2/støttefunksjoner/lister";
import {erLikIgnorerCasing} from "felles-ng2/støttefunksjoner/strenger";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";

/** GruppeMedlemListeModell.navn inneholder enten navn, eller epost dersom navn angitt i invitasjon er null.
 * Returnerer her navn angitt i Invitasjon.navn */
export function navnFraMedlemModell(medlem: GruppeMedlemListeModell): LitenIkkeTomStreng | null {
    return medlem.navn === medlem.epost ? null : medlem.navn;
}

/** returnerer medlemmer som har tilgang og er knyttet til bruker */
export function medlemmerMedTilgangMedBrukertilknytning(medlemmer: GruppeMedlemListeModell[]) {
    return medlemmer.filter(medlem => medlem.medlemstatus == Medlemstatus.normal_tilgang && medlem.invitasjonsstatus === Invitasjonsstatus.akseptert);
}

/** returnerer medlemmer som IKKE har tilgang og er knyttet til bruker */
export function medlemmerUtenTilgangMedBrukertilknytning(medlemmer: GruppeMedlemListeModell[]) {
    return medlemmer.filter(medlem => medlem.medlemstatus == Medlemstatus.tilgang_stengt && medlem.invitasjonsstatus === Invitasjonsstatus.akseptert);
}

/** returnerer medlemmer som har tilgang og IKKE er knyttet til bruker, normalt kalt invitasjoner */
export function medlemmerMedTilgangUtenBrukertilknytning(medlemmer: GruppeMedlemListeModell[]) {
    return medlemmer.filter(medlem => medlem.medlemstatus == Medlemstatus.normal_tilgang && medlem.invitasjonsstatus != Invitasjonsstatus.akseptert);
}

/** returnerer medlemmer som ikke har blitt anonymisert  */
export function medlemmerIkkeAnonymisert(medlemmer: GruppeMedlemListeModell[]) {
    return medlemmer.filter(medlem => medlem.medlemstatus != Medlemstatus.anonymisert);
}

/** returnerer medlemmer som har blitt anonymisert, disse har ikke tilgang og er ikke tilknyttet noen bruker */
export function medlemmerAnonymisert(medlemmer: GruppeMedlemListeModell[]) {
    return medlemmer.filter(medlem => medlem.medlemstatus == Medlemstatus.anonymisert);
}

/** returnerer medlemmer som er aktive og ikke har avslått invitasjon, speiler no.systemfabrikken.lederplan.domene.gruppe.Medlemmer.aktive */
export function medlemmerAktive(medlemmer: GruppeMedlemListeModell[]) {
    return medlemmer.filter(medlem => erMedlemAktiv(medlem));
}

/** returnerer sann dersom angitt medlem er aktive og ikke har avslått invitasjon, speiler reglene som no.systemfabrikken.lederplan.domene.gruppe.Medlemmer.aktive benytter */
export function erMedlemAktiv(medlem: GruppeMedlemListeModell) {
    return medlem.harTilgang && /*samme som medlem.medlemstatus == Medlemstatus.normal_tilgang*/
      medlem.invitasjonsstatus != Invitasjonsstatus.avslått;
}

/** ser etter angitt epost blant angitte medlemmer */
export function eksistererMedlemMedEpost(medlemmer: GruppeMedlemListeModell[], epost: string): ValiderEpostRespons {
    const medEpost = medlemmer
      .filter(medlem => erLikIgnorerCasing(medlem.epost, epost));
    return {
        eksistererMedlem: medlemmerMedTilgangMedBrukertilknytning(medEpost).length != 0,
        eksistererInaktivt: medlemmerUtenTilgangMedBrukertilknytning(medEpost).length != 0,
        eksistererInvitasjon: medlemmerMedTilgangUtenBrukertilknytning(medEpost).length != 0
    };
}

/** ser etter medlem med angitt navn blant angitte medlemmer */
export function eksistererMedlemMedNavn(medlemmer: GruppeMedlemListeModell[], navn: string): boolean {
    return medlemmer.some(medlem => erLikIgnorerCasing(medlem.navn, navn));
}

/** Returnerer navnet til medlemmet i angitt `medlemmer` som har angitt `medlemId`, null dersom intet medlem funnet. */
export function finnMedlemnavn(medlemmer: GruppeMedlemListeModell[], medlemId: MedlemId): string | null {
    const medlem = medlemmer.find(medlem => medlem.id == medlemId);
    return erIkkeNil(medlem) ? navnFraMedlemModell(medlem) : null;
}

/** Returnerer den første rollen i {@link GruppeMedlemListeModell#aktiveMedlemsroller}, null dersom medlem ikke har noen aktive roller */
export function primærMedlemsrolle(medlem: GruppeMedlemListeModell): MedlemsRollePeriode2Modell | null {
    return første(medlem.aktiveMedlemsroller);
}

/** Returnerer den første rollen i {@link GruppeMedlemListeModell#aktiveMedlemsroller}, tom streng dersom medlem ikke har noen aktive roller */
export function primærMedlemsrolleSomStreng(medlem: GruppeMedlemListeModell): string {
    let rolle = første(medlem.aktiveMedlemsroller);
    if (erNil(rolle)) return '';
    return rolle.tittel;
}


/** Sortere på aktiv primær-medlemsrolle, så på navn.
 * Samme sortering som no.systemfabrikken.lederplan.domene.gruppe.Medlem.compareToRolleindeksOgNavn() */
export function sorterMedlemPåAktivMedlemsrolleOgNavn(a: GruppeMedlemListeModell, b: GruppeMedlemListeModell): number {
    if (a === b) return 0;
    const påRolle = (primærMedlemsrolle(a)?.indeks ?? Number.MAX_SAFE_INTEGER) - (primærMedlemsrolle(b)?.indeks ?? Number.MAX_SAFE_INTEGER);
    if (påRolle != 0) return påRolle;
    return sorterMedlemPåNavn(a, b);
}

/** Sortere på navn */
export function sorterMedlemPåNavn(a: GruppeMedlemListeModell, b: GruppeMedlemListeModell): number {
    if (a === b) return 0;
    const påNavn = a.navn.toLocaleLowerCase().localeCompare(b.navn.toLocaleLowerCase())
    if (påNavn != 0) return påNavn;
    return a.id.toLocaleLowerCase().localeCompare(b.id.toLocaleLowerCase());
}

export function aktiveRollePerioderSomStreng(medlem: GruppeMedlemListeModell): string {
    return medlem.aktiveMedlemsroller.map(rp => rp.tittel).join(', ');
}

export interface MedlemsRolle {
    id: MedlemsRolleId;
    tittel: LitenIkkeTomStreng;
    indeks: number;
}

/** Grupperer medlemmer på deres aktive primær-rolle, sortert etter medlem.navn
 * Medlemmer uten aktivt primær-rolle vil knyttet til innslaget med medlemsrolle=null.
 * Gruppene er sortert etter rolle.indeks */
export function medlemmerGruppertPåPrimærRolle(medlemmer: GruppeMedlemListeModell[]): [MedlemsRolle | null, GruppeMedlemListeModell[]][] {
    const gruppertPåId = medlemmer.reduce((gruppert, medlem, indeks, liste) => {
        const rolleId = primærMedlemsrolle(medlem)?.medlemsrolleId || '';
        (gruppert[rolleId] || (gruppert[rolleId] = [])).push(medlem);
        return gruppert;
    }, {} as { [rollenavn: string]: GruppeMedlemListeModell[] });

    const rolleOgMedlemmer = Object.values(gruppertPåId).map(medlemmer => {
        const rolleperiode = primærMedlemsrolle(medlemmer[0]);
        const rolle: MedlemsRolle | null = erIkkeNil(rolleperiode) ? {
            id: rolleperiode.medlemsrolleId,
            tittel: rolleperiode.tittel,
            indeks: rolleperiode.indeks,
        } : null;
        return [rolle, medlemmer.sort(sorterMedlemPåNavn)] as [MedlemsRolle | null, GruppeMedlemListeModell[]];
    });

    return rolleOgMedlemmer.sort((a, b) => rolleindeks(a[0]) - rolleindeks(b[0]));
}

function rolleindeks(rolle: MedlemsRolle | null) {
    return rolle?.indeks ?? Number.MAX_SAFE_INTEGER;
}

export function muligeTilgangsrollerForMedlem(): TilgangsrollerMedlemInfo[] {
    return [
        new TilgangsrollerMedlemInfo(TilgangsrolleMedlem.lese, null, true, false),
        new TilgangsrollerMedlemInfo(TilgangsrolleMedlem.skrive, TilgangsrolleMedlem.tilgangIkkePublisert, false, false),
        new TilgangsrollerMedlemInfo(TilgangsrolleMedlem.tilgangIkkePublisert, null, false, true),
        new TilgangsrollerMedlemInfo(TilgangsrolleMedlem.tilgangSlettetIHendelseslogg, null, false, true)
    ];
}

/** Returnerer en {@link MedlemAssosiasjonerModell} instans med alle felt satt til 0/false */
export function tomMedlemAssosiasjonerModell(): MedlemAssosiasjonerModell {
    return {
        antTilknyttetMoeteSomArrangoer: 0,
        antTilknyttetAvholdtEllerLaastMoeteSomDeltaker: 0,
        antTilknyttetFremtidigIkkelaastMoeteSomDeltaker: 0,
        antTilknyttetAvholdtEllerLaastProtokollSomDeltaker: 0,
        antTilknyttetFremtidigIkkelaastProtokollSomDeltaker: 0,
        antTilknyttetAvholdtEllerLaastSakSomAnsvarlig: 0,
        antTilknyttetFremtidigIkkelaastSakSomAnsvarlig: 0,
        antTilknyttetOppgaverSomAnsvarlig: 0,
        antTilknyttetKommentarerSomForfatter: 0,
        antTilknyttetDokument: 0,
        antTilknyttetDokumentSomSignatarSomIkkeHarSignert: 0,
        erTilknyttetMalmoete: false
    }
}

export class TilgangsrollerMedlemInfo {

    readonly rolle: TilgangsrolleMedlem;
    /** navn på enum-innslaget, slik det er definert i {@link TilgangsrolleMedlem} */
    readonly enumNavn: string;
    /** tilleggssrolle som automatisk aktiveres om rolle aktiveres*/
    readonly impliserer: TilgangsrolleMedlem | null;
    /** sann dersom denne rollen ikke kan fjernes fra brukere */
    readonly obligatorisk: boolean;
    /** sann dersom bruker ikke selv kan fjerne denne rollen fra seg selv */
    readonly kanFjernesFraEgenBruker: boolean;

    constructor(rolle: TilgangsrolleMedlem, impliserer: TilgangsrolleMedlem | null, obligatorisk: boolean, kanFjernesFraEgenBruker: boolean) {
        this.rolle = rolle;
        this.enumNavn = navnPåEnuminnslag(rolle, TilgangsrolleMedlem);
        this.impliserer = impliserer;
        this.obligatorisk = obligatorisk;
        this.kanFjernesFraEgenBruker = kanFjernesFraEgenBruker;
    }

    tittel(oversettelser: EnumOversettelser): string {
        return oversettelser.tilgangsrolleMedlem(this.rolle);
    }

    subtittel(oversettelser: EnumOversettelser): string {
        return oversettelser.tilgangsrolleMedlem(this.rolle, 'subtittel');
    }
}

export type ValiderEpostRespons = {
    eksistererMedlem: boolean;
    eksistererInvitasjon: boolean;
    eksistererInaktivt: boolean;
}
